import React from 'react';
import styled from 'styled-components';
import { Row, Col, Card, Skeleton, Carousel } from 'antd';
import { SecondarySectionTitle } from '../styles';

import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';
import LazyLoad from 'react-lazyload';

import Center from '../../stories/helpers/center';
import wpImageUrl from '../../lib/wp-image-url';
import { useMedia } from '../../utils/hooks';
import { device } from '../../lib/theme';

const PROMOS_QUERY = gql`
  query Promos($slug: [String]) {
    promoTypes(where: { slug: $slug }) {
      edges {
        node {
          promos(where: { status: PUBLISH }, first: 4) {
            nodes {
              id
              featuredImage {
                node {
                  mediaItemUrl
                }
              }
              title
              promoACF {
                linkText
                url
              }
              content
            }
          }
        }
      }
    }
  }
`;

export const PromoSectionContainer = styled(Row)`
  padding: 8px 0;

  .ant-card-body {
    min-height: ${({ longForm }: { longForm: boolean }) =>
      longForm ? 300 : 160}px;
    position: relative;
  }
  .ant-card-meta-title {
    margin-bottom: 0 !important;
  }
  .ant-card-meta-description {
    font-family: 'CG-regular', sans-serif;
    font-size: 16px;
    line-height: 20px;
    color: #2c2c2c;
    min-height: 140px;
  }

  .ant-card .promo-action {
    background: transparent;
    border: none;
    color: #cd2127;
    font-family: 'CG-bold', sans-serif;
    font-weight: 400;
    font-size: 16px;
    padding: 0;
    display: flex;
    align-items: center;
    position: absolute;
    bottom: 24px;
  }

  .ant-card .promo-action > img {
    margin-left: 8px;
  }

  .ant-carousel {
    width: 100%;
  }

  @media ${device.tablet} {
    padding: 64px 0;
  }
`;

const { Meta } = Card;

const emptyPromos = new Array(4).fill('');

const positionAbsolute: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

const PromoCarousel = styled(Carousel)`
  width: 100%;
  margin-bottom: 64px;

  .slick-dots-bottom {
    bottom: -24px;
  }
  .slick-dots li button {
    background: #aaa;
  }
  .slick-dots li.slick-active button {
    background: #cd2127;
  }
`;

const PromoSection: React.FC<{
  slug?: string;
  promos?: Array<any>;
  removeButtons?: boolean;
  longForm?: boolean;
}> = ({ slug, promos, removeButtons = false, longForm = false }) => {
  const { loading, error, data } = useQuery(PROMOS_QUERY, {
    variables: { slug },
  });
  const carouselPromos = useMedia([`(max-width: 500px)`], [true], false);

  if (!promos && loading) {
    return (
      <Row>
        <Col xs={{ span: 22, offset: 1 }} xl={{ span: 20, offset: 2 }}>
          <PromoSectionContainer
            gutter={[16, 16]}
            style={{ margin: 0 }}
            longForm={longForm}
          >
            {emptyPromos.map((_, idx) => (
              <Col xs={{ span: 12 }} lg={{ span: 6 }} key={idx}>
                <Card
                  hoverable
                  cover={
                    <div
                      className="ant-skeleton ant-skeleton-active"
                      style={{
                        paddingTop: '45%',
                        position: 'relative',
                      }}
                    >
                      <div
                        className="ant-skeleton-content"
                        style={positionAbsolute}
                      >
                        <ul
                          className="ant-skeleton-paragraph"
                          style={{ ...positionAbsolute, marginBottom: 0 }}
                        >
                          <li
                            style={{ ...positionAbsolute, height: 'auto' }}
                          ></li>
                        </ul>
                      </div>
                    </div>
                  }
                >
                  <Skeleton active />
                  {!removeButtons ? <Skeleton.Button active /> : null}
                </Card>
              </Col>
            ))}
          </PromoSectionContainer>
        </Col>
      </Row>
    );
  }

  if (!promos && error) {
    console.error(error);
    return (
      <Row>
        <Col xs={{ span: 22, offset: 1 }} xl={{ span: 20, offset: 2 }}>
          <Center>Please disable Ad Blockers.</Center>
        </Col>
      </Row>
    );
  }

  const promosList =
    promos ||
    (data
      ? data.promoTypes.edges[0].node.promos.nodes.map((node: any) => ({
          id: node.id,
          title: node.title,
          description: node.content,
          img: wpImageUrl(node?.featuredImage?.node?.mediaItemUrl || ''),
          linkText: node.promoACF.linkText,
          url: node.promoACF.url,
        }))
      : []);

  const displayRowPromos = promosList.map((promo) => (
    <Col xs={{ span: 24 }} sm={{ span: 12 }} lg={{ span: 6 }} key={promo.id}>
      <LazyLoad height={350} once>
        <Card
          hoverable
          cover={
            <div
              style={{
                backgroundImage: `url(${promo.img})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                paddingTop: '45%',
              }}
            />
          }
        >
          <Meta
            title={
              promo.title ? (
                <SecondarySectionTitle marginBottom={16}>
                  {promo.title}
                </SecondarySectionTitle>
              ) : null
            }
            description={
              <div dangerouslySetInnerHTML={{ __html: promo.description }} />
            }
          />
          {!removeButtons ? (
            <a className="promo-action" href={promo.url}>
              {promo.linkText ? promo.linkText : 'Learn more'}
              <img src="/img/icon/right-arrow.png" />
            </a>
          ) : null}
        </Card>
      </LazyLoad>
    </Col>
  ));

  return (
    <Row>
      <Col xs={{ span: 22, offset: 1 }} xl={{ span: 20, offset: 2 }}>
        <PromoSectionContainer
          gutter={[16, 16]}
          style={{ margin: 0 }}
          longForm={longForm}
        >
          {carouselPromos ? (
            <PromoCarousel
              autoplay={false}
              lazyLoad="progressive"
              dotPosition="bottom"
              autoplaySpeed={6000}
              speed={1500}
            >
              {displayRowPromos}
            </PromoCarousel>
          ) : (
            displayRowPromos
          )}
        </PromoSectionContainer>
      </Col>
    </Row>
  );
};

export default PromoSection;
